import { cn } from '@systemdox/utils';

/**
 * A document's lifecycle status — the draft → ready → implemented → archived
 * axis a spec moves through. This is ONE axis: it does not encode publish state
 * (that is `PublishedBadge`, a separate chip). Splitting them out of the old
 * `SpecStatusBadge` — which drove both through one lookup with a silent
 * `|| draft` fallback (sdx#1120) — means an unrecognised value now reads as
 * exactly that (a neutral "unknown" chip), never a mislabelled "draft".
 */

export type LifecycleStatus = 'draft' | 'ready' | 'implemented' | 'archived';

const STYLES: Record<LifecycleStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  implemented: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  archived: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
};

// Deliberately distinct from every known style so an unexpected status is
// visible as unknown, not silently painted as a draft.
const UNKNOWN = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

function isLifecycleStatus(status: string): status is LifecycleStatus {
  return status in STYLES;
}

export function LifecycleBadge({ status }: { status: string }) {
  return (
    <span className={cn(
      'px-2 py-0.5 rounded-full text-xs font-medium capitalize',
      isLifecycleStatus(status) ? STYLES[status] : UNKNOWN,
    )}>
      {status}
    </span>
  );
}
